// EventPage.js
import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { getNextOccurrence } from "../utils/time"; // from next-window.js
import { formatEventCost } from "../utils/eventCost";

const weekdayNames = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
};

const ordinalNames = {
    1: "first",
    2: "second",
    3: "third",
    4: "fourth",
    5: "fifth",
};

function formatList(items) {
    const clean = items.filter(Boolean);
    if (clean.length <= 1) return clean[0] || "";
    if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
    return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
}

function getRecurrenceLabel(recurrence = {}) {
    const type = recurrence.type || "one-time";
    const config = recurrence.config || {};
    const weekdays = Array.isArray(config.weekdays)
        ? config.weekdays.map((day) => weekdayNames[day]).filter(Boolean)
        : [];
    const weekdayLabel = formatList(weekdays);

    if (type === "one-time") return "One-time event";
    if (type === "weekly") {
        return weekdayLabel ? `Every ${weekdayLabel}` : "Weekly event";
    }
    if (type === "bi-weekly") {
        return weekdayLabel ? `Every other ${weekdayLabel}` : "Bi-weekly event";
    }
    if (type === "every-x-days") {
        const interval = Number(config.interval) || 1;
        return interval === 1 ? "Every day" : `Every ${interval} days`;
    }
    if (type === "monthly-nth") {
        const nths = Array.isArray(config.nths)
            ? config.nths.map((nth) => ordinalNames[nth]).filter(Boolean)
            : [];
        const nthLabel = formatList(nths);

        if (nthLabel && weekdayLabel) {
            return `Every ${nthLabel} ${weekdayLabel} of the month`;
        }
        if (nthLabel) return `Every ${nthLabel} occurrence of the month`;
        return weekdayLabel ? `Monthly on ${weekdayLabel}` : "Monthly event";
    }

    return "Event";
}

export default function EventPage() {
    const { slug: eventSlug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        async function fetchEventBySlug() {
            try {
                if (!eventSlug) {
                    throw new Error("Missing event slug in URL");
                }

                setLoading(true);
                setError(null);

                const res = await fetch(
                    `/.netlify/functions/get-event-by-slug?slug=${encodeURIComponent(
                        eventSlug
                    )}`
                );
                const json = await res.json();

                if (!res.ok || !json.ok) {
                    throw new Error(json.error || "Failed to load event");
                }

                if (isMounted) {
                    setEvent(json.event);
                }
            } catch (err) {
                console.error("Error loading event", err);
                if (isMounted) {
                    setError(err.message || "Failed to load event");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        fetchEventBySlug();

        return () => {
            isMounted = false;
        };
    }, [eventSlug]);

    // Loading state
    if (loading) {
        return (
            <main className="page event-page" aria-labelledby="event-heading">
                <p className="page-status">Loading event…</p>
            </main>
        );
    }

    // Error state
    if (error) {
        return (
            <main className="page page--contained event-page" aria-labelledby="event-heading">
                <h1 id="event-heading" className="page-title">Event could not be loaded</h1>
                <p className="page-status">{error}</p>
            </main>
        );
    }

    // No event (just in case)
    if (!event) {
        return (
            <main className="page page--contained event-page" aria-labelledby="event-heading">
                <h1 id="event-heading" className="page-title">Event not found</h1>
                <p className="page-status">We couldn&apos;t find this event.</p>
            </main>
        );
    }

    // ---- Time / occurrence calculations ----
    let next = null;
    try {
        next = getNextOccurrence(event);
    } catch (err) {
        console.error("Error computing next occurrence:", err);
    }

    const tz = event.timezone || "America/Toronto";

    // Fallback for one-time events (or if recurrence logic returns null)
    if (!next && event.baseStartUTC) {
        const startLocal = DateTime.fromISO(event.baseStartUTC, { zone: "utc" }).setZone(tz);

        // Prefer endsUTC if present; otherwise compute from durationMinutes
        let endLocal = null;
        if (event.endsUTC) {
            endLocal = DateTime.fromISO(event.endsUTC, { zone: "utc" }).setZone(tz);
        } else if (Number.isFinite(event.durationMinutes)) {
            endLocal = startLocal.plus({ minutes: event.durationMinutes });
        }

        // Only use fallback if the start is valid (and optionally in the future)
        if (startLocal.isValid) {
            next = {
                startISO: event.baseStartUTC,
                startLocal,
                endLocal,
                endLocalISO: event.endsUTC || (endLocal?.toUTC().toISO() ?? null),
            };
        }
    }

    let baseStartLocal = null;
    if (event.baseStartUTC) {
        baseStartLocal = DateTime.fromISO(event.baseStartUTC, {
            zone: "utc",
        }).setZone(tz);
    }

    const nowLocal = DateTime.now().setZone(tz);
    const hasStarted =
        baseStartLocal && baseStartLocal.isValid
            ? nowLocal >= baseStartLocal
            : false;

    const startRowLabel = hasStarted ? "Next" : "Starts";

    const startsISO = next?.startISO ?? null;

    // tzShort must be defined in outer scope (so Ends can use it)
    let tzShort = "";
    if (next?.startLocal?.isValid) {
        tzShort = next.startLocal.offsetNameShort || next.startLocal.toFormat("ZZZ");
    }

    // Starts: date + time (local)
    const startsLabel = next?.startLocal?.isValid
        ? `${next.startLocal.toFormat("cccc, LLLL d, yyyy")} · ${next.startLocal.toFormat("h:mm a")}${tzShort ? ` (${tzShort})` : ""}`
        : "No upcoming sessions";

    const dateLabel = next?.startLocal?.isValid
        ? next.startLocal.toFormat("LLLL d, yyyy")
        : "No upcoming sessions";

    // Time: start time only (local) + tz short
    let timeLabel = "Time to be announced";
    if (next?.startLocal?.isValid) {
        const startTime = next.startLocal.toFormat("h:mm a");
        timeLabel = `${startTime}${tzShort ? ` (${tzShort})` : ""}`;
    }

    // Duration label (from durationMinutes)
    let durationLabel = "Duration to be announced";

    if (Number.isFinite(event.durationMinutes)) {
        const hours = event.durationMinutes / 60;
        durationLabel =
            Number.isInteger(hours) && hours >= 1
                ? `${hours} ${hours === 1 ? "hour" : "hours"}`
                : `${event.durationMinutes} minutes`;
    }



    // Ends: date + time (local) — only if endLocal exists + valid
    const ends =
        next?.endLocal?.isValid
            ? `${next.endLocal.toFormat("cccc, LLLL d, yyyy")} · ${next.endLocal.toFormat("h:mm a")}${tzShort ? ` (${tzShort})` : ""}`
            : null;

    // Is this a single-day event?
    const isSingleDayEvent =
        next?.startLocal?.isValid &&
        next?.endLocal?.isValid &&
        next.startLocal.hasSame(next.endLocal, "day");


    const coverImage = event.img || event.eventImageUrl;

    const cost = formatEventCost(event.cost);

    const link = event.link || event.eventLink;
    const hasRegistrationForm = !!event?.customForm?.enabled;
    const isRegistered = new URLSearchParams(location.search).get("registered") === "1";
    const recurrenceLabel = getRecurrenceLabel(event.recurrence);
    const eventTypeLabel = Array.isArray(event.tags) && event.tags.length > 0
        ? event.tags[0]
        : recurrenceLabel;

    const summaryItems = [
        {
            label: startRowLabel,
            value: startsISO ? dateLabel : startsLabel,
            dateTime: startsISO,
        },
        {
            label: isSingleDayEvent ? "Duration" : "Time",
            value: isSingleDayEvent ? durationLabel : timeLabel,
        },
        event.host
            ? { label: "Host", value: event.host }
            : { label: "Format", value: recurrenceLabel },
        { label: "Cost", value: cost },
    ];

    const detailRows = [
        {
            label: startRowLabel,
            value: startsLabel,
            dateTime: startsISO,
        },
        {
            label: isSingleDayEvent ? "Duration" : "Time",
            value: isSingleDayEvent ? durationLabel : timeLabel,
        },
        ...(ends ? [{ label: "Ends", value: ends }] : []),
        ...(event.host ? [{ label: "Host", value: event.host }] : []),
        { label: "Cost", value: cost },
    ];

    const handleRegister = () => {
        const returnTo = encodeURIComponent(location.pathname);
        navigate(`/event/${encodeURIComponent(eventSlug)}/checkout?returnTo=${returnTo}`);
    };

    const renderRegistrationButton = () => (
        <button
            type="button"
            className={
                isRegistered
                    ? "event-cover-cta event-cover-cta--registered"
                    : "event-cover-cta"
            }
            onClick={handleRegister}
            disabled={isRegistered}
        >
            {isRegistered && (
                <span className="event-cover-cta__icon" aria-hidden="true">
                    ✓
                </span>
            )}
            <span>{isRegistered ? "You're registered" : "Register for event"}</span>
        </button>
    );

    return (
        <main className="event-page" aria-labelledby="event-heading">
            {/* COVER SECTION */}
            <div
                className="event-cover"
                style={coverImage ? { backgroundImage: `url(${coverImage})` } : {}}
            >
                <div className="event-cover-inner">
                    <span className="event-cover-kicker">{eventTypeLabel}</span>
                    <h1 className="event-cover-title" id="event-heading">
                        {event.name}
                    </h1>

                    {event.excerpt && (
                        <p className="event-cover-excerpt">{event.excerpt}</p>
                    )}

                    {hasRegistrationForm && renderRegistrationButton()}
                    {!hasRegistrationForm && (
                        <p className="event-cover-start">
                        {startsISO ? (
                            <time dateTime={startsISO}>{startsLabel}</time>
                        ) : (
                            <span>{startsLabel}</span>
                        )}
                        </p>
                    )}
                </div>
            </div>

            <section className="event-summary-strip" aria-label="Event summary">
                <div className="event-summary-grid">
                    {summaryItems.map((item) => (
                        <div className="event-summary-item" key={item.label}>
                            <span className="event-summary-label">{item.label}</span>
                            {item.dateTime ? (
                                <time className="event-summary-value" dateTime={item.dateTime}>{item.value}</time>
                            ) : (
                                <span className="event-summary-value">{item.value}</span>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* HEADER / DETAILS */}
            <section className="page-header event-header" aria-label="Event details">
                <div className="event-main-layout">
                    {/* LEFT: body + table */}
                    <section className="event-main-primary">
                        {event.eventBody && (
                            <section aria-label="Event details">
                                <div
                                    className="event-body-content"
                                    dangerouslySetInnerHTML={{ __html: event.eventBody }}
                                />
                            </section>
                        )}
                    </section>

                    <aside className="event-details-panel" aria-label="Event registration and details">
                        <div className="event-details-panel-inner">
                            <span className="event-details-label">{eventTypeLabel}</span>
                            <h2>Event Details</h2>

                            <dl className="event-detail-list">
                                {detailRows.map((item) => (
                                    <div className="event-detail-row" key={item.label}>
                                        <dt>{item.label}</dt>
                                        <dd>
                                            {item.dateTime ? (
                                                <time dateTime={item.dateTime}>{item.value}</time>
                                            ) : (
                                                item.value
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>

                            {hasRegistrationForm && (
                                <div className="event-panel-action">
                                    {renderRegistrationButton()}
                                </div>
                            )}

                            {!hasRegistrationForm && link && (
                                <a
                                    className="event-panel-link"
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Join / Details
                                </a>
                            )}
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
}
